import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, ArrowUpRight, Sparkles, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";

export default function Area() {
  const { user, loading, login } = useAuth();
  const [ent, setEnt] = useState(null);

  useEffect(() => {
    if (loading || !user) return;
    api.get("/me/entitlements").then((r) => setEnt(r.data)).catch(() => setEnt({}));
  }, [user, loading]);

  if (!loading && !user) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-6" data-testid="area-login">
        <h1 className="font-serif text-3xl mb-4">La tua area personale</h1>
        <p className="text-[#8C7E83] mb-8">Accedi per ritrovare i tuoi acquisti.</p>
        <button onClick={login} className="inline-flex items-center gap-2 bg-[#C6899E] text-[#FBF5F1] rounded-full px-7 py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-[#B4718C]">
          <LogIn className="w-4 h-4" /> Accedi
        </button>
      </div>
    );
  }

  const hasProgram = ent?.program;
  const nothing = ent && !hasProgram && !(ent.products?.length) && !(ent.courses?.length) && !ent.premium;

  return (
    <div data-testid="area-page">
      <PageHeader eyebrow="Area personale" title={user?.name ? `Ciao, ${user.name.split(" ")[0]}` : "Il mio spazio"} desc="I tuoi acquisti e i tuoi percorsi, sempre qui." />
      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-16 pb-24">
        {nothing ? (
          <EmptyState title="Ancora nessun acquisto" desc="Quando acquisti un prodotto lo ritroverai qui." icon={ShoppingBag} />
        ) : (
          <div className="grid sm:grid-cols-2 gap-6">
            {hasProgram && (
              <Link to="/percorso" data-testid="area-program-card"
                className="hover-lift group rounded-2xl border border-[rgba(198,137,158,0.4)] bg-[#FFFFFF] p-7 flex items-start justify-between gap-4">
                <div>
                  <div className="eyebrow mb-3 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Il mio prodotto</div>
                  <h3 className="font-serif text-2xl text-[#3D3238] group-hover:text-[#C6899E] transition-colors">eBook + Percorso 21 giorni</h3>
                  <p className="mt-2 text-sm text-[#8C7E83]">Riprendi da dove eri.</p>
                </div>
                <div className="w-10 h-10 rounded-full border border-[rgba(198,137,158,0.4)] flex items-center justify-center group-hover:bg-[#C6899E] transition-colors">
                  <ArrowUpRight className="w-4 h-4 text-[#C6899E] group-hover:text-[#FBF5F1] transition-colors" />
                </div>
              </Link>
            )}
            {ent?.premium && (
              <Link to="/premium" className="hover-lift group rounded-2xl border border-[rgba(61,50,56,0.1)] bg-[#FFFFFF] p-7">
                <div className="eyebrow mb-3">Premium</div>
                <h3 className="font-serif text-2xl text-[#3D3238] group-hover:text-[#C6899E] transition-colors">Abbonamento attivo</h3>
              </Link>
            )}
            {(ent?.courses || []).map((cid) => (
              <Link key={cid} to={`/corsi/${cid}`} className="hover-lift group rounded-2xl border border-[rgba(61,50,56,0.1)] bg-[#FFFFFF] p-7">
                <div className="eyebrow mb-3">Corso</div>
                <h3 className="font-serif text-2xl text-[#3D3238] group-hover:text-[#C6899E] transition-colors">Apri il corso</h3>
              </Link>
            ))}
            {(ent?.products || []).map((pid) => (
              <Link key={pid} to={`/shop/${pid}`} className="hover-lift group rounded-2xl border border-[rgba(61,50,56,0.1)] bg-[#FFFFFF] p-7">
                <div className="eyebrow mb-3">Prodotto</div>
                <h3 className="font-serif text-2xl text-[#3D3238] group-hover:text-[#C6899E] transition-colors">Apri il prodotto</h3>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
